import { instanceBo } from "../utils/axios.create";
import type { MeasurementPresentation } from "./programInsights.service";

/**
 * SIMS-8555 — how a program arranges its dashboard or statistics surface.
 *
 * Mirrors the server's `arrangement.ts`. The server resolves every entry on read
 * (orphaned, invalid presentation, no shape) and enforces every rule again on
 * write; this client shows what it is given and sends back what the author chose.
 * Reading and saving need the program-maintenance permission (PROG_MNG_VIEW).
 */

export type ArrangementSurface = "PROGRAM_DASHBOARD" | "PARTICIPANT_STATISTICS";

export const SURFACES: ArrangementSurface[] = [
	"PROGRAM_DASHBOARD",
	"PARTICIPANT_STATISTICS",
];

export type ProgramFigure =
	| "INCENTIVES_EARNED"
	| "INCENTIVES_RECEIVED"
	| "INCENTIVES_BANKED"
	| "SHARE_OF_AVAILABLE"
	| "ENROLLMENTS_BY_STATUS"
	| "PROVIDERS_ENROLLED"
	| "AVERAGE_LENGTH_OF_TREATMENT";

/** Every program-wide figure, in the overview's own order (the server's default). */
export const PROGRAM_FIGURES: readonly ProgramFigure[] = [
	"INCENTIVES_EARNED",
	"INCENTIVES_RECEIVED",
	"INCENTIVES_BANKED",
	"SHARE_OF_AVAILABLE",
	"ENROLLMENTS_BY_STATUS",
	"PROVIDERS_ENROLLED",
	"AVERAGE_LENGTH_OF_TREATMENT",
];

export type ArrangedEntryState =
	| "OK"
	| "ORPHANED"
	| "INVALID_PRESENTATION"
	| "NO_SHAPE";

export type UnavailableReason =
	| "NEEDS_TARGET_AND_PER_DAY"
	| "NO_VALUE_TREND_PER_OCCURRENCE"
	| "DOES_NOT_SUM"
	| "TALLY_TOTAL_IS_COUNT"
	| "NO_CATEGORIES"
	| "COVERAGE_NOT_A_PRESENTATION"
	| "REDUNDANT_ON_RUNNING_TOTAL"
	| "MEANINGLESS_PER_OCCURRENCE_COUNT"
	| "NO_LATEST_CATEGORY"
	| "CATEGORY_NOT_A_NUMBER"
	| "CATEGORY_HAS_NO_TARGET";

export type ReasonField = "comparator" | "frequency" | "quantity";

export interface PresentationOffer {
	presentation: MeasurementPresentation;
	offered: boolean;
	isDefault: boolean;
	reason?: UnavailableReason;
	field?: ReasonField;
}

export interface OfferedResult {
	id: string;
	display: string;
}

export interface DeclaredAs {
	quantity: string;
	unit: string;
	frequency: string;
	comparator: string;
}

export interface StoredChoice {
	presentation: MeasurementPresentation;
	summary: MeasurementPresentation | null;
	resultId: string | null;
}

export interface AuthorEntry {
	/** `${eventId}:${measurementId}`: the measurement's own key on every surface. */
	key: string;
	eventId: string;
	/**
	 * The measurement's id: an entry follows it through reorders, edits and other
	 * removals (AC16). Null only on an entry stored before measurements were keyed
	 * by id; it names nothing and can't be sent back.
	 */
	measurementId: string | null;
	/** Null when orphaned: the event or the declaration is gone. */
	eventName: string | null;
	name: string | null;
	state: ArrangedEntryState;
	/** What the author chose, exactly as stored. Null for a draft or unarranged entry. */
	stored: StoredChoice | null;
	/** What the dashboard draws: the stored choice when valid, else the default. */
	presentation: MeasurementPresentation | null;
	defaultPresentation: MeasurementPresentation | null;
	/** All seven; the unavailable ones carry the reason and the field to change (AC4). */
	presentations: PresentationOffer[];
	/** Summaries a trend may carry (AC12). "None" is always valid. */
	summaries: MeasurementPresentation[];
	/** AC13: offered only on an occurrence trend whose event has configured results. */
	results: OfferedResult[];
	/** What a trend may carry, whatever is drawn now: offered once the author picks a trend. */
	trend: { summaries: MeasurementPresentation[]; results: OfferedResult[] };
	/** The declaration as configured (8b "Declared as"). Null when orphaned. */
	declaredAs: DeclaredAs | null;
	/**
	 * SIMS-8579 (Q-5: author surfaces name the role): the role id a viewer must hold
	 * to receive it, or null when it is not restricted.
	 */
	accessRequirement?: string | null;
	summaryValid: boolean;
	resultValid: boolean;
}

export interface AuthorSection {
	id: string;
	name: string;
	entries: AuthorEntry[];
}

export interface AuthorView {
	surface: ArrangementSurface;
	/** False: nothing is stored yet, and `sections` is an unsaved draft (AC1). */
	arranged: boolean;
	updatedAt: string | null;
	sections: AuthorSection[];
	/** Declarations the arrangement doesn't place; the dashboard still draws them. */
	unarranged: AuthorEntry[];
	figures: { arranged: ProgramFigure[]; available: ProgramFigure[] };
	/** SIMS-8562: null for the draft of a surface never arranged. */
	id: string | null;
	/** Null only on the arrangement SIMS-8555's first save created, until renamed. */
	name: string | null;
	/** The account's roles it is for; empty: every role. */
	roleIds: string[];
}

/** SIMS-8562: one of a surface's arrangements, in list order (first match wins). */
export interface ArrangementListItem {
	id: string;
	name: string | null;
	roleIds: string[];
	order: number;
	updatedAt: string | null;
}

export interface ArrangementList {
	surface: ArrangementSurface;
	arrangements: ArrangementListItem[];
}

export interface PutArrangedEntry {
	eventId: string;
	measurementId: string;
	presentation: MeasurementPresentation;
	summary?: MeasurementPresentation;
	resultId?: string;
}

export interface PutArrangement {
	sections: { id: string; name: string; entries: PutArrangedEntry[] }[];
	figures: ProgramFigure[];
	/** SIMS-8562: the arrangement saved; omitted, the first unrestricted one. */
	id?: string;
	name?: string;
	roleIds?: string[];
}

/** The server's refusal of a save: each problem names the section or entry. */
export interface ArrangementRefusal {
	statusCode?: number;
	message?: string | string[];
	problems?: string[];
}

/**
 * The server's body, with the HTTP status added: a 400 that lists `problems` carries
 * no `statusCode` of its own (the global filter passes an object body through as is).
 * A refusal with no JSON body (a gateway's 403) still carries its status, so it reads
 * as a refusal and is not retried. A request that got no answer is passed on as is.
 */
const refusal = (err: {
	response?: { status?: number; data?: unknown };
}): Promise<never> => {
	if (!err.response) return Promise.reject(err);
	const data = err.response.data;
	return Promise.reject({
		statusCode: err.response.status,
		...(data && typeof data === "object" ? (data as ArrangementRefusal) : {}),
	});
};

const path = (programId: string, surface: ArrangementSurface): string =>
	`/dashboard/programs/${encodeURIComponent(programId)}/arrangements/${surface}`;

export const getArrangement = async (
	programId: string,
	surface: ArrangementSurface,
	id?: string,
): Promise<AuthorView> =>
	// instanceBo's baseURL already carries `/v1/accounts/{accountId}`.
	instanceBo
		.get(path(programId, surface), id ? { params: { id } } : undefined)
		.then((res) => res.data.body as AuthorView)
		.catch(refusal);

export const putArrangement = async (
	programId: string,
	surface: ArrangementSurface,
	body: PutArrangement,
): Promise<AuthorView> =>
	instanceBo
		.put(path(programId, surface), body)
		.then((res) => res.data.body as AuthorView)
		.catch(refusal);

// ── SIMS-8562: a surface's arrangements ─────────────────────────────────────

const listOf = (res: { data: { body: unknown } }): ArrangementList =>
	res.data.body as ArrangementList;

export const listArrangements = async (
	programId: string,
	surface: ArrangementSurface,
): Promise<ArrangementList> =>
	instanceBo
		.get(`${path(programId, surface)}/list`)
		.then(listOf)
		.catch(refusal);

export const createArrangement = async (
	programId: string,
	surface: ArrangementSurface,
	body: { name: string; roleIds: string[] },
): Promise<ArrangementList> =>
	instanceBo
		.post(`${path(programId, surface)}/list`, body)
		.then(listOf)
		.catch(refusal);

export const reorderArrangements = async (
	programId: string,
	surface: ArrangementSurface,
	ids: string[],
): Promise<ArrangementList> =>
	instanceBo
		.put(`${path(programId, surface)}/order`, { ids })
		.then(listOf)
		.catch(refusal);

export const removeArrangement = async (
	programId: string,
	surface: ArrangementSurface,
	id: string,
): Promise<ArrangementList> =>
	instanceBo
		.delete(`${path(programId, surface)}/${encodeURIComponent(id)}`)
		.then(listOf)
		.catch(refusal);
